import axios from "axios";
import store from "../reduxData/store";
import { toast } from "react-toastify";
import { TOKENS_UPDATE } from "../reduxData/auth/authTypes";
import { authStorage } from "./authStorage";
import { clear_state_on_logout } from "../reduxData/auth/authAction";
import { ROUTE_CONFIG } from "../config/routeConfig";

const baseURL = process.env.REACT_APP_BASE_URL;
const apiClient = axios.create({ baseURL });

// Helpers

const getAuthState = () => store.getState().auth;

const showToast = (message, id) => {
  if (!toast.isActive(id)) toast.error(message, { toastId: id });
};

// helper: match "/item-details/:id" with real pathname
const isRouteMatch = (routePath, currentPath) => {
  const routeSegments = routePath.split("/").filter(Boolean);
  const urlSegments = currentPath.split("/").filter(Boolean);

  if (routeSegments.length !== urlSegments.length) {
    return false;
  }

  for (let i = 0; i < routeSegments.length; i++) {
    const routePart = routeSegments[i];
    const urlPart = urlSegments[i];

    if (routePart.startsWith(":")) {
      continue;
    }

    if (routePart !== urlPart) {
      return false;
    }
  }

  return true;
};

const getModuleFromPath = (pathname) => {
  const route = ROUTE_CONFIG.find((item) =>
    isRouteMatch(item.path, pathname)
  );

  return route ? route.module_name : null;
};

// Request Interceptor
apiClient.interceptors.request.use((config) => {
  const { accessToken, refreshtoken } = getAuthState();
  const moduleName = getModuleFromPath(window.location.pathname);
  if (accessToken) config.headers["x-access-token"] = accessToken;
  if (refreshtoken) config.headers["refreshtoken"] = refreshtoken;
  if (moduleName) config.headers["module_name"] = moduleName;
  return config;
});

let isRefreshing = false;
let pendingRequests = [];

const resolveQueue = (newToken) => {
  pendingRequests.forEach(({ resolve }) => resolve(newToken));
  pendingRequests = [];
};

const rejectQueue = (error) => {
  pendingRequests.forEach(({ reject }) => reject(error));
  pendingRequests = [];
};

const refreshAccessToken = async () => {
  const { refreshtoken } = getAuthState();

  if (!refreshtoken) throw new Error("No refresh token available.");

  const { data } = await axios.get(`${baseURL}/user/authorization`, {
    headers: { refreshtoken },
  });

  if (!data?.status || !data.data?.token) {
    throw new Error("Token refresh failed — invalid response.");
  }

  const newAccessToken = data.data.token;
  const newRefreshToken = data.data.refreshToken ?? refreshtoken;

  // Single place that writes tokens — authStorage handles localStorage,
  // Redux dispatch keeps in-memory state in sync
  authStorage.updateTokens(newAccessToken, newRefreshToken);
  store.dispatch({ type: TOKENS_UPDATE, payload: data.data });

  return newAccessToken;
};

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config: originalRequest, response } = error;
    const status = response?.status;
    if (status === 401) {
      clear_state_on_logout();
      return Promise.reject(error);
    }
    if (status === 403) {
      showToast("You don't have permission for this action.", "forbidden");
    }
    if (status >= 500) {
      showToast("A server error occurred. Please try again later.", "serverError");
    }
    if (!response) {
      showToast("Cannot connect to server. Check your network connection.", "networkError");
    }
    return Promise.reject(error);
  }
);

export default apiClient;